import { ContainerStatus } from "./container.type"

export interface PilotDashboardStatistics {
    waste_diversion_rate: number;
    total_revenue: number;
    total_orders: number;
    completion_rate: number;
}

export interface DailyWasteAmount {
    date: string;
    waste_type: string;
    amount: number;
}

export interface RecyclerSupply {
    recycler_name: string;
    supply_amount: number;
}

export interface MaterialBreakdown {
    material_type: string;
    amount: number;
}

export interface WasteTypeWithMaterials {
    waste_type: string;
    total_kg: number;
    materials: MaterialBreakdown[];
}

export interface TruckLocation {
    truck_id: string;
    truck_name: string;
    latitude: number;
    longitude: number;
    last_updated: string;
    status: 'active' | 'idle' | 'maintenance';
    current_load: number;
    capacity: number;
}

export interface ContainerAlarmState {
    full: boolean | null;
    fire: boolean | null;
    tilt: boolean | null;
    battery: boolean | null;
}

export interface ContainerMapItem {
    container_id: string;
    container_name: string;
    latitude: number | null;
    longitude: number | null;
    status: ContainerStatus;
    fill_level: number | null;
    capacity: number | null;
    capacity_unit: string;
    alarms: ContainerAlarmState;
    material_type_id: string | null;
}

export interface ContainerData {
    container_id: string;
    container_name: string;
    status: ContainerStatus;
    latitude: number | null;
    longitude: number | null;
    fill_level: number; // Always in KG
    capacity: number;
    capacity_unit: string; // e.g., "tons", "kg"
    alarms: ContainerAlarmState;
  }

export interface DashboardStatistics {
    // Current month metrics
    time_from_full_to_collection: number;
    time_from_collection_to_delivery: number;
    full_percentage: number;
    recycler_appetite: number;
    truck_driver_count: number;
    supply_per_recycler: RecyclerSupply[];
    sustainable_supply: number;
    collection_frequency: number;
    average_container_fill_rate: number;
    waste_diversion_rate: number;
    average_incentive_per_kg: number;
    trips_today_count: number;
    incoming_waste_breakdown: DailyWasteAmount[];
    waste_type_material_breakdown: WasteTypeWithMaterials[];
    total_revenue: number;
    total_revenue_last_month: number;
    average_processing_time: number;
    quality_compliance_rate: number;
    rejected_waste_percentage: number;
    processing_capacity_utilization: number;
    active_trucks: TruckLocation[];
    average_route_efficiency: number;
    fuel_consumption_per_trip: number;
    maintenance_schedule_compliance: number;
    driver_performance_score: number;
    customer_satisfaction_score: number;
    waste_compaction_efficiency: number;
    storage_capacity_utilization: number;
    emergency_collection_requests: number;
    waste_quality_rating: number;

    // Pickup Order Statistics
    total_orders: number;
    pending_orders: number;
    completed_orders: number;
    cancelled_orders: number;
    completion_rate: number;
    cancellation_rate: number;
    total_waste_amount: number;
    completed_waste_amount: number;
    average_order_amount: number;
    orders_today: number;
    average_processing_time_hours: number;

    // Container Operations
    total_containers: number;
    active_containers: number;
    full_containers: number;
    containers_needing_attention: number;
    capacity_utilization_rate: number;
    containers: ContainerMapItem[];

    // Previous month metrics
    previous_month_time_from_full_to_collection: number;
    previous_month_time_from_collection_to_delivery: number;
    previous_month_full_percentage: number;
    previous_month_recycler_appetite: number;
    previous_month_truck_driver_count: number;
    previous_month_supply_per_recycler: RecyclerSupply[];
    previous_month_sustainable_supply: number;
    previous_month_collection_frequency: number;
    previous_month_average_container_fill_rate: number;
    previous_month_waste_diversion_rate: number;
    previous_month_average_incentive_per_kg: number;
    previous_month_trips_today_count: number;
    previous_month_incoming_waste_breakdown: DailyWasteAmount[];
    previous_month_waste_type_material_breakdown: WasteTypeWithMaterials[];
    previous_month_total_revenue: number;
    previous_month_total_revenue_last_month: number;
    previous_month_average_processing_time: number;
    previous_month_quality_compliance_rate: number;
    previous_month_rejected_waste_percentage: number;
    previous_month_processing_capacity_utilization: number;
    previous_month_average_route_efficiency: number;
    previous_month_fuel_consumption_per_trip: number;
    previous_month_maintenance_schedule_compliance: number;
    previous_month_driver_performance_score: number;
    previous_month_customer_satisfaction_score: number;
    previous_month_waste_compaction_efficiency: number;
    previous_month_storage_capacity_utilization: number;
    previous_month_emergency_collection_requests: number;
    previous_month_waste_quality_rating: number;
    previous_month_total_orders: number;
    previous_month_pending_orders: number;
    previous_month_completed_orders: number;
    previous_month_cancelled_orders: number;
    previous_month_completion_rate: number;
    previous_month_cancellation_rate: number;
    previous_month_total_waste_amount: number;
    previous_month_completed_waste_amount: number;
    previous_month_average_order_amount: number;
    previous_month_orders_today: number;
    previous_month_average_processing_time_hours: number;
}

export interface DashboardQueryParams {
    waste_outlet_id: string;
    month_date?: string;
}

/** Per collection-site operational row for branch analysis dashboard. */
export interface BranchOperationalRow {
    collection_site_id: string;
    name: string | null;
    latitude: number | null;
    longitude: number | null;
    branch_contact: string | null;
    total_pickup_orders: number;
    total_waste_kg: number;
    completed_waste_kg: number;
    total_revenue: number;
    waste_diversion_rate_percent: number;
    employee_count: number;
    container_count: number;
    fill_level_report_count: number;
}

/** Org-scoped by-branch operational dashboard response. */
export interface DashboardByBranchResponse {
    organization_id: string;
    month_date: string;
    total_pickup_orders: number;
    total_waste_kg: number;
    completed_waste_kg: number;
    total_revenue: number;
    waste_diversion_rate_percent: number;
    previous_month_total_pickup_orders?: number;
    previous_month_total_waste_kg?: number;
    previous_month_completed_waste_kg?: number;
    previous_month_total_revenue?: number;
    previous_month_waste_diversion_rate_percent?: number;
    branches: BranchOperationalRow[];
    unassigned_waste: BranchOperationalRow | null;
}

export enum StatisticType {
    TIME_FROM_FULL_TO_COLLECTION = 'time_from_full_to_collection',
    TIME_FROM_COLLECTION_TO_DELIVERY = 'time_from_collection_to_delivery',
    FULL_PERCENTAGE = 'full_percentage',
    RECYCLER_APPETITE = 'recycler_appetite',
    TRUCK_DRIVER_COUNT = 'truck_driver_count',
    SUPPLY_PER_RECYCLER = 'supply_per_recycler',
    SUSTAINABLE_SUPPLY = 'sustainable_supply',
    COLLECTION_FREQUENCY = 'collection_frequency',
    AVERAGE_CONTAINER_FILL_RATE = 'average_container_fill_rate',
    WASTE_DIVERSION_RATE = 'waste_diversion_rate',
    AVERAGE_INCENTIVE_PER_KG = 'average_incentive_per_kg',
    TRIPS_TODAY_COUNT = 'trips_today_count',
    INCOMING_WASTE_BREAKDOWN = 'outgoing_waste_breakdown',
    WASTE_TYPE_BREAKDOWN = 'waste_type_material_breakdown',
    TOTAL_REVENUE_LAST_MONTH = 'total_revenue_last_month',
    AVERAGE_PROCESSING_TIME = 'average_processing_time',
    QUALITY_COMPLIANCE_RATE = 'quality_compliance_rate',
    REJECTED_WASTE_PERCENTAGE = 'rejected_waste_percentage',
    PROCESSING_CAPACITY_UTILIZATION = 'processing_capacity_utilization',
    ACTIVE_TRUCKS = 'active_trucks',
    AVERAGE_ROUTE_EFFICIENCY = 'average_route_efficiency',
    FUEL_CONSUMPTION_PER_TRIP = 'fuel_consumption_per_trip',
    MAINTENANCE_SCHEDULE_COMPLIANCE = 'maintenance_schedule_compliance',
    DRIVER_PERFORMANCE_SCORE = 'driver_performance_score',
    CUSTOMER_SATISFACTION_SCORE = 'customer_satisfaction_score',
    WASTE_COMPACTION_EFFICIENCY = 'waste_compaction_efficiency',
    STORAGE_CAPACITY_UTILIZATION = 'storage_capacity_utilization',
    EMERGENCY_COLLECTION_REQUESTS = 'emergency_collection_requests',
    TOTAL_REVENUE = 'total_revenue',
    WASTE_QUALITY_RATING = 'waste_quality_rating',
    TOTAL_ORDERS = 'total_orders',
    COMPLETION_RATE = 'completion_rate',
    CANCELLATION_RATE = 'cancellation_rate',
}
